import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import config from '../config';
import '../styles/UsageBanner.css';

const API_URL = config.apiBaseUrl;
const PRICE_PER_5000_LINES = 999; // $999 per 5000 lines
const PAY_AS_YOU_GO_MODAL_KEY = 'payAsYouGoModalShown';

const UsageBanner = () => {
  const { user, uploadUsage, resetUploadUsage } = useContext(AuthContext);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [lineQuantity, setLineQuantity] = useState(5000);
  const [paymentAmount, setPaymentAmount] = useState(PRICE_PER_5000_LINES);
  const [processing, setProcessing] = useState(false);
  const [paymentType, setPaymentType] = useState(''); // 'upgrade' or 'buymore'

  // Update payment amount when line quantity changes
  useEffect(() => {
    if (paymentType === 'upgrade') {
      setPaymentAmount(PRICE_PER_5000_LINES);
    } else {
      const multiplier = Math.ceil(lineQuantity / 5000);
      setPaymentAmount(multiplier * PRICE_PER_5000_LINES);
    }
  }, [lineQuantity, paymentType]);

  // Auto-open Buy More Lines modal when tier 2 with 0 lines (Pay As You Go signup)
  useEffect(() => {
    if (!uploadUsage || uploadUsage.tier !== 2) return;
    const hasNoLines = (uploadUsage.remainingLines === 0 && uploadUsage.linesLimit === 0) ||
      (uploadUsage.remainingLines === 0 && uploadUsage.linesLimit !== undefined);
    if (!hasNoLines) return;
    if (sessionStorage.getItem(PAY_AS_YOU_GO_MODAL_KEY)) return;
    setPaymentType('buymore');
    setLineQuantity(5000);
    setPaymentAmount(PRICE_PER_5000_LINES);
    setShowPaymentModal(true);
    sessionStorage.setItem(PAY_AS_YOU_GO_MODAL_KEY, '1');
  }, [uploadUsage]);

  const handleOpenPaymentModal = (type) => {
    setPaymentType(type);
    if (type === 'upgrade') {
      setLineQuantity(5000);
      setPaymentAmount(PRICE_PER_5000_LINES);
    } else {
      setLineQuantity(5000);
      setPaymentAmount(PRICE_PER_5000_LINES);
    }
    setShowPaymentModal(true);
  };

  const handleQuantityChange = (quantity) => {
    const lines = parseInt(quantity, 10);
    if (!isNaN(lines)) setLineQuantity(lines);
  };

  const handleStripeCheckout = async (e) => {
    e.preventDefault();
    if (!user?.email) return;
    try {
      setProcessing(true);
      const returnBaseUrl = `${window.location.origin}/app`;
      const response = await axios.post(`${API_URL}/api/stripe/create-checkout-session`, {
        email: user.email,
        lineQuantity,
        returnBaseUrl
      });
      const url = response.data?.url;
      if (url) {
        window.location.href = url;
        return;
      }
      alert('Could not start checkout. Please try again.');
    } catch (err) {
      console.error('Error:', err);
      const msg = err.response?.data?.error || err.message;
      alert(`Error: ${msg}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpgradeContactAdmin = async (e) => {
    e.preventDefault();
    setShowPaymentModal(false);
    alert('Thank you for your payment request. Please contact the administrator to complete your account upgrade.');
  };

  if (!uploadUsage) {
    return null; // Don't show banner if no usage data is available
  }

  // Don't show banner for users with unlimited access
  if (uploadUsage.tier > 2) {
    return null;
  }

  // Use safe numbers so banner never shows NaN/undefined during refetch after payment
  const linesLimit = Number(uploadUsage.linesLimit) || 0;
  const linesUsed = Number(uploadUsage.linesUsed) || 0;
  const remainingFromApi = Number(uploadUsage.remainingLines);
  const safeRemaining = Number.isFinite(remainingFromApi) && remainingFromApi >= 0
    ? remainingFromApi
    : Math.max(0, linesLimit - linesUsed);

  const percentUsed = linesLimit > 0
    ? Math.min(100, Math.round((linesUsed / linesLimit) * 100))
    : 0;
  const isWarning = percentUsed > 80;
  const isCritical = percentUsed > 95;
  const isZeroLines = linesLimit === 0 || (safeRemaining === 0 && linesLimit !== undefined);

  const renderPaymentModalContent = () => {
    if (paymentType === 'upgrade') {
      return (
        <>
          <h3>Upgrade to Premium (Tier 2)</h3>
          <p>Upgrade to our Premium tier to remove time limitations and get more flexible upload options.</p>
          <div className="payment-instructions">
            <h4>Payment Instructions:</h4>
            <ol>
              <li>Go to <a href="https://paypal.me/hkassamnath" target="_blank" rel="noopener noreferrer">paypal.me/hkassamnath</a></li>
              <li>Enter the amount: <strong>${paymentAmount}</strong></li>
              <li>In the payment note, include your email address and payment purpose: <strong>Account Upgrade</strong></li>
              <li>Once payment is confirmed, we&apos;ll upgrade your account</li>
            </ol>
            <p className="payment-contact">For questions, contact <a href="mailto:payments@rekxup.com">payments@rekxup.com</a></p>
          </div>
          <div className="payment-actions">
            <button type="button" onClick={() => setShowPaymentModal(false)} className="close-btn">Close</button>
            <button type="button" onClick={handleUpgradeContactAdmin} className="primary-btn">I&apos;ve sent payment</button>
          </div>
        </>
      );
    }
    // Buy More Lines – Stripe
    return (
      <>
        <h3>Purchase Additional Upload Credits</h3>
        <p>Add more lines to your account. You will be redirected to Stripe to complete payment securely.</p>
        <form onSubmit={handleStripeCheckout}>
          <div className="form-group">
            <label>Number of lines to purchase:</label>
            <div className="quantity-selector">
              <button type="button" className="quantity-btn" onClick={() => handleQuantityChange(Math.max(5000, lineQuantity - 5000))}>-</button>
              <input
                type="number"
                min="5000"
                step="5000"
                value={lineQuantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                required
              />
              <button type="button" className="quantity-btn" onClick={() => handleQuantityChange(lineQuantity + 5000)}>+</button>
            </div>
          </div>
          <div className="form-group">
            <p className="fixed-price">Payment Amount: <strong>${paymentAmount}</strong></p>
            <span className="price-note">$999 per 5,000 lines ({lineQuantity.toLocaleString()} lines = ${paymentAmount})</span>
          </div>
          <div className="payment-actions">
            <button type="button" onClick={() => setShowPaymentModal(false)} className="close-btn">Close</button>
            <button type="submit" className="stripe-pay-btn" disabled={processing}>
              {processing ? (
                <>
                  <span className="stripe-pay-btn-spinner" aria-hidden />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  <span className="stripe-pay-btn-icon" aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  Pay with Stripe
                </>
              )}
            </button>
          </div>
        </form>
      </>
    );
  };

  return (
    <>
      <div className={`usage-banner ${isWarning ? 'warning' : ''} ${isCritical ? 'critical' : ''}`}>
        <div className="usage-info">
          <div className="usage-text">
            <span className="usage-title">Upload Usage:</span>
            <span className="usage-value">
              {isZeroLines
                ? '0 lines remaining – purchase lines to get started'
                : `${safeRemaining.toLocaleString()} of ${linesLimit.toLocaleString()} lines remaining`}
            </span>
            {uploadUsage.tier === 1 && uploadUsage.daysRemaining && (
              <span className="usage-expiry">
                Trial expires in {uploadUsage.daysRemaining} days
              </span>
            )}
          </div>
          <div className="usage-progress-container">
            <div 
              className="usage-progress-bar" 
              style={{ width: `${percentUsed}%` }}
            ></div>
          </div>
        </div>
        
        <div className="usage-actions">
          {/* Show upgrade button for tier 1 users */}
          {uploadUsage.tier === 1 && (
            <button 
              className="upgrade-btn"
              onClick={() => handleOpenPaymentModal('upgrade')}
            >
              Upgrade Now
            </button>
          )}
          
          {/* Show buy more lines button for tier 2 users */}
          {uploadUsage.tier === 2 && (
            <button 
              className="reset-usage-btn"
              onClick={() => handleOpenPaymentModal('buymore')}
            >
              Buy More Lines
            </button>
          )}
        </div>
      </div>

      {showPaymentModal && (
        <div className="payment-modal-overlay">
          <div className="payment-modal">
            {renderPaymentModalContent()}
          </div>
        </div>
      )}
    </>
  );
};

export default UsageBanner;